import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { Terms } from "../components/terms";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Link } from "react-router-dom";
import FullScreenLoader from "../components/loader";
import queryString from "query-string";
import {
  CATEGORY_PARAM_TO_DATA_CATEGORY,
  CATEGORY_PARAM_TO_SELECTED_CAT,
  GENERAL_EDUCATION_AWARDS,
  HIGHER_EDUCATION_SPECIALIZATIONS,
  getApplicationAmount,
  isGeneralEducationAward,
  requiresApplicationType,
  requiresCensus,
} from "../lib/awardConfig";

import { useNavigate } from "react-router-dom";

const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_UPLOAD_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"];

const hasAllowedUploadExtension = (filename = "") => {
  const normalizedFilename = filename.toLowerCase();

  return ALLOWED_UPLOAD_EXTENSIONS.some((extension) =>
    normalizedFilename.endsWith(extension),
  );
};

const validateUploadFile = (file) => {
  if (!file) {
    return {
      isValid: false,
      message: "Please select a file to upload.",
    };
  }

  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return {
      isValid: false,
      message: "File size is too large. Max size is 5MB.",
    };
  }

  if (!hasAllowedUploadExtension(file.name)) {
    return {
      isValid: false,
      message:
        "Please upload a valid file type. Only PDF, PNG, JPG, JPEG files are allowed.",
    };
  }

  return { isValid: true };
};

// Applications for all categories (General, Higher Education and Emerging
// Educator) are closed for 2026. Flip this to false to re-open the form.
const APPLICATIONS_CLOSED = true;

export const Apply = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    name: "",
    university: "",
    specialization: "",
    email: "",
    phone: "",
    agreementAccepted: false,
  });
  const [formDataSc, setFormDataSc] = useState({
    nic: "",
    title: "",
    full_name: "",
    phone: "",
    whatsapp_phone: "",
    email: "",
    school_province: "",
    school_education_zone: "",
    school_of_employement: "",
    award_category: "",
    application_type: "",
    census: "",
    agreementAccepted: false,
  });
  const [isOther, setIsOther] = useState(false);
  const [isPaymentSelect, setIsPaymentSelect] = useState(false);
  const [isBankDeposit, setIsBankDeposit] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [nicFile, setNicFile] = useState(null);
  const [isRecieptSubmit, setIsRecieptSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const paymentUpdateCalled = React.useRef(false);

  const location = useLocation();
  const { state, orderidparam } = useParams();
  const queryParams = queryString.parse(location.search) || {};
  const resultIndicator = queryParams.resultIndicator;
  const categoryParam = params.get("category");
  const isEmergingEducator = categoryParam === "emerging-educator";

  useEffect(() => {
    if (!categoryParam && !state) {
      // If there is no category param in the URL, and it's not a payment return route, auto-redirect to Higher Education
      navigate("/apply?category=higher-education", { replace: true });
    }
  }, [categoryParam, navigate, state]);

  useEffect(() => {
    if (isPaymentSelect) {
      // Use setTimeout to ensure the DOM has fully painted the new payment view
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [isPaymentSelect]);

  const buildHigherEducationPayload = (orderid, category) => ({
    orderid,
    category,
    ...formData,
  });

  const buildGeneralEducationPayload = (orderid) => {
    const data = {
      ...formDataSc,
      orderid,
      category: "School Category",
    };

    if (!requiresApplicationType(formDataSc.award_category)) {
      delete data.application_type;
    }

    if (
      !requiresCensus(formDataSc.award_category) ||
      formDataSc.application_type !== "school"
    ) {
      delete data.census;
    }

    return data;
  };

  const handleFileSelect = (e, setFile) => {
    const file = e.target.files?.[0];
    const validation = validateUploadFile(file);

    if (!validation.isValid) {
      alert(validation.message);
      e.target.value = "";
      setFile(null);
      return;
    }

    setFile(file);
  };

  const uploadSupportingDocument = async ({
    orderid,
    file,
    formPayload,
    uploadType,
  }) => {
    const formDataAws = new FormData();

    formDataAws.append("file", file);
    formDataAws.append("orderid", orderid);
    formDataAws.append("uploadType", uploadType);
    formDataAws.append("formdata", JSON.stringify(formPayload));

    return axios.post(
      "https://qf858c2ct0.execute-api.ap-south-1.amazonaws.com/file-upload",
      formDataAws,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  };

  const handleInputChange = (e, type) => {
    const { name, value } = e.target;
    // if (name === "university" && value === "Other") {
    //   setIsOther(true);
    // } else {
    // }
    if (type === "he") {
      setFormData({
        ...formData,
        [name]: value,
      });
    } else if (type === "sc") {
      setFormDataSc((currentFormData) => {
        if (name === "award_category") {
          return {
            ...currentFormData,
            [name]: value,
            application_type: requiresApplicationType(value)
              ? currentFormData.application_type
              : "",
            census:
              requiresCensus(value) &&
              currentFormData.application_type === "school"
                ? currentFormData.census
                : "",
          };
        }

        if (name === "application_type") {
          return {
            ...currentFormData,
            [name]: value,
            census: value === "school" ? currentFormData.census : "",
          };
        }

        return {
          ...currentFormData,
          [name]: value,
        };
      });
    }
  };

  const handlePhoneChange = (value, type) => {
    if (type === "he") {
      setFormData({
        ...formData,
        phone: value,
      });
    } else if (type === "sc") {
      setFormDataSc({
        ...formDataSc,
        phone: value,
      });
    } else if (type === "sc_w") {
      setFormDataSc({
        ...formDataSc,
        whatsapp_phone: value,
      });
    }
  };

  const handleAgreementChange = (e, type) => {
    const { name, checked } = e.target;
    if (type === "he") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else if (type === "sc") {
      setFormDataSc({
        ...formDataSc,
        [name]: checked,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (APPLICATIONS_CLOSED) {
      return;
    }

    if (isEmergingEducator && !nicFile) {
      alert("Please upload your NIC document before continuing.");
      return;
    }

    setIsLoading(true);
    const orderid = generateOrderId();
    let data = {};
    const dataCat = CATEGORY_PARAM_TO_DATA_CATEGORY[categoryParam];

    if (!dataCat) {
      console.log("Please select a category.");
      setIsLoading(false);
      return null;
    }

    if (selectedCat === "he") {
      data = buildHigherEducationPayload(orderid, dataCat);
    } else if (selectedCat === "sc") {
      data = buildGeneralEducationPayload(orderid);
    } else {
      console.log("Please select a category.");
      setIsLoading(false);
      return null;
    }

    try {
      await axios.post(
        "https://qf858c2ct0.execute-api.ap-south-1.amazonaws.com/save-user",
        data,
      );

      if (isEmergingEducator) {
        await uploadSupportingDocument({
          orderid,
          file: nicFile,
          formPayload: data,
          uploadType: "nic",
        });
      }

      setIsPaymentSelect(true);
    } catch (error) {
      console.error("Error initiating payment:", error);
    } finally {
      setIsLoading(false);
    }

    // Scroll to top of page
  };

  const onReceiptSubmit = async (e) => {
    e.preventDefault();

    if (!receiptFile) {
      alert("Please upload your payment receipt before submitting.");
      return;
    }

    setIsLoading(true);

    try {
      await uploadSupportingDocument({
        orderid: orderId,
        file: receiptFile,
        formPayload:
          selectedCat === "sc"
            ? buildGeneralEducationPayload(orderId)
            : buildHigherEducationPayload(
                orderId,
                CATEGORY_PARAM_TO_DATA_CATEGORY[categoryParam],
              ),
        uploadType: "receipt",
      });
      setIsRecieptSubmit(true);
    } catch (error) {
      console.error("Error uploading receipt:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateOrderId = () => {
    const currentDate = new Date();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const hours = String(currentDate.getHours()).padStart(2, "0");
    const minutes = String(currentDate.getMinutes()).padStart(2, "0");
    const seconds = String(currentDate.getSeconds()).padStart(2, "0");
    const randomNum = String(Math.floor(Math.random() * 100000)).padStart(
      5,
      "0",
    );
    const orderId = `NEA-${month}-${day}-${hours}-${minutes}-${seconds}-${randomNum}`;
    setOrderId(orderId);
    return orderId;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if ((state && orderidparam) || resultIndicator) {
      const storageKey = `payment_updated_${orderidparam}`;
      if (paymentUpdateCalled.current || sessionStorage.getItem(storageKey)) {
        return;
      }

      paymentUpdateCalled.current = true;
      sessionStorage.setItem(storageKey, "true");

      axios
        .patch(
          `https://qf858c2ct0.execute-api.ap-south-1.amazonaws.com/update-payment`,
          {
            orderid: orderidparam,
            result_indicator: resultIndicator ? resultIndicator : "",
            state: state,
          },
        )
        .then((response) => {
          console.log(response);
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        })
        .catch((error) => {
          console.error("Error updating payment status:", error);
        });
    }
  }, []);

  useEffect(() => {
    const category = params.get("category");
    const award = params.get("award");
    const applicationType = params.get("type");

    // Block direct access to General Education applications by redirecting
    if (category === "general-education") {
      navigate("/apply?category=higher-education", { replace: true });
      return;
    }

    setSelectedCat(CATEGORY_PARAM_TO_SELECTED_CAT[category] || null);

    if (award && isGeneralEducationAward(award)) {
      setFormDataSc((currentFormData) => ({
        ...currentFormData,
        award_category: award,
        application_type: requiresApplicationType(award)
          ? applicationType || currentFormData.application_type
          : "",
        census:
          requiresCensus(award) &&
          (applicationType || currentFormData.application_type) === "school"
            ? currentFormData.census
            : "",
      }));
    } else if (award) {
      setFormData((currentFormData) => ({
        ...currentFormData,
        specialization: award,
      }));
    }
  }, [search, navigate]);

  const startPaymentRequest = async () => {
    if (sessionId) {
      console.log("Payment session already initialized.");
      return;
    }

    setIsLoading(true);
    const amount = getApplicationAmount({
      selectedCat,
      award: formDataSc.award_category,
      applicationType: formDataSc.application_type,
    });
    const requestData = {
      orderid: orderId,
      amount,
      category: selectedCat,
      award:
        selectedCat === "sc"
          ? formDataSc.award_category
          : formData.specialization,
      application_type: formDataSc.application_type,
      currency: "LKR",
      description: "national education award 2024 - application fee",
      name: selectedCat === "sc" ? formDataSc.full_name : formData.name,
      email: selectedCat === "sc" ? formDataSc.email : formData.email,
    };

    axios
      .post(
        `https://qf858c2ct0.execute-api.ap-south-1.amazonaws.com/get-session-id`,
        requestData,
      )
      .then((response) => {
        const sessionData = response.data.data;
        setIsLoading(false);
        console.log(sessionData);
        // Extract the session ID from the response
        const session_id = sessionData.id;

        // Set the session ID in the component's state
        setSessionId(session_id);
        console.log(session_id);
        // Call Checkout.configure with the session ID and any other necessary configuration
        window &&
          window.Checkout.configure({
            session: {
              id: session_id,
            },
          });

        // Start the payment process
        window && window.Checkout.showEmbeddedPage("#embed-target");
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error initiating payment:", error);
      });
  };

  const applicationAmount = getApplicationAmount({
    selectedCat,
    award: formDataSc.award_category,
    applicationType: formDataSc.application_type,
  });

  const showApplicationTypeField =
    selectedCat === "sc" && requiresApplicationType(formDataSc.award_category);
  const showCensusField =
    selectedCat === "sc" &&
    requiresCensus(formDataSc.award_category) &&
    formDataSc.application_type === "school";

  return (
    <div className="apply-bg-color w-full h-full min-h-screen py-10 lg:py-6 relative">
      {isLoading && <FullScreenLoader />}
      <img
        src={
          selectedCat === "he"
            ? "/assets/img/higher-award.webp"
            : selectedCat === "sc"
              ? "/assets/img/general-award.webp"
              : "/assets/img/award.webp"
        }
        alt="Award Background"
        className={
          selectedCat === "he" || selectedCat === "sc"
            ? "absolute brightness-50 inset-0 w-full h-full  object-cover object-[90%_16%]"
            : "fixed z-5 w-[300px] right-[25%]"
        }
      />
      <h1
        className="text-3xl font-bold text-[#dd7538] sm:text-5xl capitalize tracking-wider pt-16 pb-20 px-4 lg:px-0 section-title relative mx-auto w-fit z-20"
        style={{ fontFamily: "Poppins" }}
        data-aos="fade-down"
        data-aos-delay="100"
      >
        {state === "cancel"
          ? "PAYMENT CANCELLED"
          : state === "timeout"
            ? "TIMEOUT"
            : state === "success"
              ? "PAYMENT SUCCESS"
              : isRecieptSubmit
                ? "DEPOSIT CONFIRMED"
                : APPLICATIONS_CLOSED
                  ? "Applications Closed"
                  : "Apply Now"}
      </h1>
      <div
        className="max-w-xl mx-auto my-10 px-4 relative"
        style={{ zIndex: 60 }}
      >
        {state === "cancel" ? (
          <CancelMessage />
        ) : state === "timeout" ? (
          <TimeoutMessage />
        ) : state === "success" ? (
          <SuccesstMessage selectedCat={selectedCat} />
        ) : APPLICATIONS_CLOSED ? (
          <ApplicationsClosedMessage />
        ) : !isPaymentSelect ? (
          <div>
            <div className="category-buttons flex justify-center py-2">
              {selectedCat !== "sc" && (
                <div
                  className="group relative flex flex-col items-center gap-4 p-6 rounded-2xl transition-all duration-300"
                  data-aos="fade-up"
                  data-aos-delay="600"
                  data-aos-once="true"
                >
                  {/* Main Button */}
                  <button
                    type="button"
                    onClick={() => setSelectedCat("he")}
                    className={`
          relative z-10 px-8 py-3 rounded-xl font-bold tracking-wide transition-all duration-300
          ${
            selectedCat === "he"
              ? "bg-orange-600 text-white shadow-sm shadow-orange-600/40 scale-105"
              : "bg-white/10 text-orange-100 border border-orange-600/30 hover:bg-orange-600/10"
          }
        `}
                  >
                    Higher Education
                  </button>

                  {/* Badge Section */}
                  <div className="flex flex-col items-center">
                    {/* The container uses a subtle glow and border-gradient effect */}
                    <span className="relative inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-gradient-to-b from-orange-500/20 to-orange-500/5 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.15em] text-orange-100 shadow-[0_0_15px_rgba(249,115,22,0.1)] backdrop-blur-md">
                      {/* Animated Status Dot */}
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full  rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500"></span>
                      </span>

                      {/* Badge Text */}
                      {!isEmergingEducator
                        ? "National Educator"
                        : "Emerging Educator"}

                      {/* Subtle Shine Overlay */}
                      <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/10 to-transparent pointer-events-none"></span>
                    </span>
                  </div>

                  {/* Decorative Glow - Optional for extra "Modern" feel */}
                  {selectedCat === "he" && (
                    <div className="absolute inset-0 bg-orange-500/5 blur-2xl rounded-full -z-10" />
                  )}
                </div>
              )}
            </div>
            {selectedCat === "he" ? (
              <form onSubmit={handleSubmit} className="apply-form">
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="100"
                >
                  <label
                    htmlFor="title"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Title
                  </label>
                  <select
                    id="title"
                    name="title"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "he")}
                    value={formData.title}
                    required
                  >
                    <option value="">Select Title</option>
                    <option value="Prof">Prof</option>
                    <option value="Dr">Dr</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                  </select>
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="200"
                >
                  <label
                    htmlFor="name"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "he")}
                    value={formData.name}
                    required
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="300"
                >
                  <label
                    htmlFor="university"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    University
                  </label>
                  <input
                    type="text"
                    id="university"
                    name="university"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "he")}
                    value={formData.university}
                    required
                  />
                  {/* <select
              id="university"
              name="university"
              className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
              onChange={(e)=>handleInputChange(e, 'he')}
              value={formData.university}
              required
            >
              <option value="">Select University</option>
              <option value="Colombo Moratuwa">Colombo Moratuwa</option>
              <option value="Peradeniya">Peradeniya</option>
              <option value="IIT">IIT</option>
              <option value="Rajarata University">Rajarata University</option>
              <option value="Kelaniya">Kelaniya</option>
              Add more universities here
              <option value="Other">Other</option>
            </select> */}
                </div>
                {isOther && (
                  <div
                    className="mb-4"
                    data-aos="fade-up"
                    data-aos-once="true"
                    data-aos-delay="100"
                  >
                    <label
                      htmlFor="customUniversity"
                      className="block text-gray-300 text-sm font-semibold"
                    >
                      Other University
                    </label>
                    <input
                      type="text"
                      id="customUniversity"
                      name="university"
                      className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                      onChange={(e) => handleInputChange(e, "he")}
                      value={formData.university}
                      required
                    />
                  </div>
                )}
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="400"
                >
                  <label
                    htmlFor="specialization"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Specialization
                  </label>
                  <select
                    id="specialization"
                    name="specialization"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "he")}
                    value={formData.specialization}
                    required
                  >
                    <option value="">Select Specialization</option>
                    {HIGHER_EDUCATION_SPECIALIZATIONS.map((specialization) => (
                      <option key={specialization} value={specialization}>
                        {specialization}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="500"
                >
                  {isEmergingEducator && (
                    <div className="mb-4">
                      <label
                        htmlFor="nic_upload"
                        className="block text-gray-300 text-sm font-semibold"
                      >
                        NIC Upload
                      </label>
                      <input
                        type="file"
                        id="nic_upload"
                        name="nic_upload"
                        className="apply-field h-[64px] mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50 file:mr-4 file:border-0 file:bg-[#dd7538] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white file:rounded-md file:items-center"
                        onChange={(e) => handleFileSelect(e, setNicFile)}
                        accept=".jpg,.jpeg,.png,.pdf"
                        required
                      />
                      <p className="mt-2 text-xs text-gray-300">
                        Accepted formats: JPG, JPEG, PNG, PDF. Maximum size 5
                        MB.
                      </p>
                      {nicFile && (
                        <p className="mt-2 text-sm text-orange-200">
                          Selected file: {nicFile.name}
                        </p>
                      )}
                    </div>
                  )}

                  <label
                    htmlFor="email"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "he")}
                    value={formData.email}
                    required
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="600"
                >
                  <label
                    htmlFor="phone"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Phone
                  </label>
                  <PhoneInput
                    inputProps={{
                      name: "phone",
                      required: true,
                      id: "phone",
                    }}
                    className="apply-field-tel mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    inputStyle={{ width: "100%" }}
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e, "he")}
                    enableSearch={true}
                    specialLabel=""
                    country={"lk"}
                  />
                  <div
                    className="my-4"
                    data-aos="fade-up"
                    data-aos-once="true"
                    data-aos-delay="800"
                  >
                    {/* Text area for agreement details */}
                    <div className="text-gray-300 text-sm font-semibold mb-2">
                      Agreement Details
                    </div>
                    <div className="apply-field border-gray-300 border rounded-md p-2">
                      {/* Use a text area or div (read-only) */}
                      <div className="w-full rounded-md h-[300px] overflow-y-scroll overflow-x-hidden">
                        <Terms type="he" />
                      </div>
                      {/* If a div is used instead of a text area, match the styles */}
                      {/* <div className="w-full bg-gray-100 rounded-md p-2">
                Include your agreement details here with clickable links.
              </div> */}
                    </div>
                  </div>
                </div>

                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-delay="300"
                  data-aos-once="true"
                >
                  {/* Download Evaluation Criteria link (visible for Higher Education / Emerging Educator) */}
                  {selectedCat === "he" && (
                    <div className="mb-3">
                      <a
                        href="https://docs.google.com/spreadsheets/d/1i_CedaHVAd8ysPWitHXXHE58LGdbdtKdDEMGwLWrQtM/export?format=xlsx"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-orange-300 bg-white/5 hover:bg-white/10 border border-yellow-300/20 px-3 py-2 rounded-md"
                        aria-label="Download Evaluation Criteria"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-yellow-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 3v12m0 0l4-4m-4 4-4-4M21 21H3"
                          />
                        </svg>
                        <span className="font-medium">
                          Download Evaluation Criteria
                        </span>
                      </a>
                    </div>
                  )}

                  {/* Checkbox for agreement acceptance */}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="agreementAccepted"
                      checked={formData.agreementAccepted}
                      onChange={(e) => handleAgreementChange(e, "he")}
                      className="form-checkbox border-gray-300 text-[#dd7538] focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                      required
                    />
                    <span className="ml-2 text-gray-300 text-sm">
                      I agree to the terms and conditions
                    </span>
                  </label>
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-delay="400"
                  data-aos-once="true"
                >
                  <button
                    type="submit"
                    className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
                  >
                    Submit and Pay
                  </button>
                </div>
              </form>
            ) : selectedCat === "sc" ? (
              <form onSubmit={handleSubmit} className="apply-form">
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="100"
                >
                  <label
                    htmlFor="nic"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    NIC
                  </label>
                  <input
                    type="text"
                    id="nic"
                    name="nic"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.nic}
                    required
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="200"
                >
                  <label
                    htmlFor="title"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Title
                  </label>
                  <select
                    id="title"
                    name="title"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.title}
                    required
                  >
                    <option value="">Select Title</option>
                    <option value="Prof">Prof</option>
                    <option value="Dr">Dr</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                  </select>
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="300"
                >
                  <label
                    htmlFor="full_name"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.full_name}
                    required
                  />
                </div>

                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="800"
                >
                  <label
                    htmlFor="phone"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Contact Number
                  </label>
                  <PhoneInput
                    inputProps={{
                      name: "phone",
                      required: true,
                      id: "phone",
                    }}
                    className="apply-field-tel mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    inputStyle={{ width: "100%" }}
                    placeholder="Enter phone number"
                    value={formDataSc.phone}
                    onChange={(e) => handlePhoneChange(e, "sc")}
                    enableSearch={true}
                    specialLabel=""
                    country={"lk"}
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="800"
                >
                  <label
                    htmlFor="whatsapp_phone"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    WhatsApp Number
                  </label>
                  <PhoneInput
                    inputProps={{
                      name: "whatsapp_phone",
                      required: false,
                      id: "whatsapp_phone",
                    }}
                    className="apply-field-tel mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    inputStyle={{ width: "100%" }}
                    placeholder="Enter WhatsApp number"
                    value={formDataSc.whatsapp_phone}
                    onChange={(e) => handlePhoneChange(e, "sc_w")}
                    enableSearch={true}
                    specialLabel=""
                    country={"lk"}
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="900"
                >
                  <label
                    htmlFor="email"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.email}
                    required
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="500"
                >
                  <label
                    htmlFor="school_province"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Province of Your School
                  </label>
                  <select
                    id="school_province"
                    name="school_province"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.school_province}
                    required
                  >
                    <option value="">Select a Province</option>
                    <option value="Western Province">Western Province</option>
                    <option value="Central Province">Central Province</option>
                    <option value="Eastern Province">Eastern Province</option>
                    <option value="Northern Province">Northern Province</option>
                    <option value="North Central Province">
                      North Central Province
                    </option>
                    <option value="North Western Province">
                      North Western Province
                    </option>
                    <option value="Sabaragamuwa Province">
                      Sabaragamuwa Province
                    </option>
                    <option value="Southern Province">Southern Province</option>
                    <option value="Uva Province">Uva Province</option>
                  </select>
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="600"
                >
                  <label
                    htmlFor="school_education_zone"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Education Zone of Your School
                  </label>
                  <input
                    type="text"
                    id="school_education_zone"
                    name="school_education_zone"
                    placeholder="Leave empty if not applicable"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.school_education_zone}
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="700"
                >
                  <label
                    htmlFor="school_of_employement"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    School of Employment
                  </label>
                  <input
                    type="text"
                    id="school_of_employement"
                    name="school_of_employement"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.school_of_employement}
                    required
                  />
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="800"
                >
                  <label
                    htmlFor="award_category"
                    className="block text-gray-300 text-sm font-semibold"
                  >
                    Award Category
                  </label>
                  <select
                    id="award_category"
                    name="award_category"
                    className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                    onChange={(e) => handleInputChange(e, "sc")}
                    value={formDataSc.award_category}
                    required
                  >
                    <option value="">Select Award Category</option>
                    {GENERAL_EDUCATION_AWARDS.map((award) => (
                      <option key={award} value={award}>
                        {award}
                      </option>
                    ))}
                  </select>
                </div>
                {showApplicationTypeField && (
                  <div
                    className="mb-4"
                    data-aos="fade-up"
                    data-aos-once="true"
                    data-aos-delay="850"
                  >
                    <label
                      htmlFor="application_type"
                      className="block text-gray-300 text-sm font-semibold"
                    >
                      Application Type
                    </label>
                    <select
                      id="application_type"
                      name="application_type"
                      className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                      onChange={(e) => handleInputChange(e, "sc")}
                      value={formDataSc.application_type}
                      required
                    >
                      <option value="">Select Application Type</option>
                      <option value="individual">Individual</option>
                      <option value="school">School</option>
                    </select>
                  </div>
                )}
                {showCensusField && (
                  <div
                    className="mb-4"
                    data-aos="fade-up"
                    data-aos-once="true"
                    data-aos-delay="875"
                  >
                    <label
                      htmlFor="census"
                      className="block text-gray-300 text-sm font-semibold"
                    >
                      School Census Number
                    </label>
                    <input
                      type="text"
                      id="census"
                      name="census"
                      className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                      onChange={(e) => handleInputChange(e, "sc")}
                      value={formDataSc.census}
                      required
                    />
                  </div>
                )}
                <div
                  className="my-4"
                  data-aos="fade-up"
                  data-aos-once="true"
                  data-aos-delay="1000"
                >
                  {/* Text area for agreement details */}
                  <div className="text-gray-300 text-sm font-semibold mb-2">
                    Agreement Details
                  </div>
                  <div className="apply-field border-gray-300 border rounded-md p-2">
                    {/* Use a text area or div (read-only) */}
                    <div className="w-full rounded-md h-[300px] overflow-y-scroll overflow-x-hidden">
                      <Terms type="sc" />
                    </div>
                    {/* If a div is used instead of a text area, match the styles */}
                    {/* <div className="w-full bg-gray-100 rounded-md p-2">
                Include your agreement details here with clickable links.
              </div> */}
                  </div>
                </div>

                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-delay="300"
                  data-aos-once="true"
                >
                  {/* Checkbox for agreement acceptance */}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="agreementAccepted"
                      checked={formDataSc.agreementAccepted}
                      onChange={(e) => handleAgreementChange(e, "sc")}
                      className="form-checkbox border-gray-300 text-[#dd7538] focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50"
                      required
                    />
                    <span className="ml-2 text-gray-300 text-sm">
                      I agree to the terms and conditions
                    </span>
                  </label>
                </div>
                <div
                  className="mb-4"
                  data-aos="fade-up"
                  data-aos-delay="400"
                  data-aos-once="true"
                >
                  <button
                    type="submit"
                    className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
                  >
                    Submit and Pay
                  </button>
                </div>
              </form>
            ) : (
              <></>
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center">
            {!isRecieptSubmit && (
              <button
                onClick={() => setIsBankDeposit(!isBankDeposit)}
                className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50 hover:bg-orange-400"
              >
                Bank Deposit
              </button>
            )}
            {isBankDeposit && (
              <div className="py-10">
                {!isRecieptSubmit ? (
                  <div className="bank-detail-form">
                    <div className="mb-4">
                      <p className="text-white ">
                        <b>Amount - </b>
                        {`LKR ${applicationAmount.toFixed(2)}`}{" "}
                      </p>
                      <p className="text-white ">
                        <b>Bank - </b>Hatton National Bank{" "}
                      </p>
                      <p className="text-white ">
                        <b>Account No. - </b> 156010007350{" "}
                      </p>
                      <p className="text-white ">
                        <b>Branch – </b>Malabe{" "}
                      </p>
                      <p className="text-white ">
                        <b>Account Name – </b>Sri Lanka Institute of Information
                        Technology (Gte) Ltd.{" "}
                      </p>
                      <p className="text-white ">
                        <b>Swift Code – </b>HBLILKLX
                      </p>
                      <br />
                      <p className="text-white ">
                        <b>Reference Code – </b>
                        <span className="text-orange-500 font-semibold">
                          {orderId}
                        </span>
                      </p>
                    </div>
                    <p className="text-orange-500 font-semibold">
                      Please use the above "Reference Code" in the
                      Description/Remark field when you are doing the payment.
                    </p>
                    <p className="text-white pt-4 pb-2 font-semibold">
                      Please upload your payment receipt after completing the
                      transaction.
                    </p>
                    <form
                      onSubmit={(e) => {
                        onReceiptSubmit(e);
                      }}
                    >
                      <label
                        className="apply-field mt-1 block w-full rounded-md border-orange-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50 hover:bg-orange-400"
                        style={{ backgroundColor: "rgba(221, 117, 56, 0.5)" }}
                      >
                        <input
                          type="file"
                          onChange={(e) => handleFileSelect(e, setReceiptFile)}
                          className="hidden"
                          accept=".jpg,.jpeg,.png,.pdf"
                          required
                        />
                        <div className="flex flex-row justify-between items-center cursor-pointer">
                          <p>Select File</p>
                          {receiptFile && (
                            <p
                              className="text-white w-full"
                              style={{ overflowWrap: "anywhere" }}
                            >
                              {receiptFile.name}
                            </p>
                          )}
                        </div>
                      </label>
                      <button
                        type="submit"
                        disabled={!receiptFile || isLoading}
                        className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
                      >
                        {isLoading ? (
                          <div className="flex flex-row items-center">
                            Submit Receipt
                            <img
                              width={30}
                              height={30}
                              priority
                              src="/assets/img/loading.svg"
                              alt="Follow us on Twitter"
                            />
                          </div>
                        ) : (
                          "Submit Receipt"
                        )}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <p
                      className="text-white pt-4 pb-2 font-semibold text-center"
                      data-aos="fade-up"
                      data-aos-delay="100"
                      data-aos-once="true"
                    >
                      Thank you for submitting your payment receipt!
                      <br />
                      Our team will review it and send a confirmation to your
                      email shortly. <br />
                      <br />
                      {selectedCat !== "sc" && (
                        <>
                          Please follow the instructions in your email to submit
                          your application for National Educator Awards 2026.
                          <br />
                        </>
                      )}
                      Please ensure to check both your inbox and spam folder for
                      our response. <br />
                      If you have any questions or need further assistance, feel
                      free to contact our support team through phone
                      <a href="tel:0117543945" className="text-[#dd7538]">
                        {" "}
                        0117543945
                      </a>{" "}
                      or send us an email to{" "}
                      <a
                        href="mailto:info@nationaleducatorawards.lk"
                        className="text-[#dd7538]"
                      >
                        info@nationaleducatorawards.lk
                      </a>
                    </p>
                    <Link
                      to={"/"}
                      className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
                    >
                      Go to Home
                    </Link>
                  </div>
                )}
              </div>
            )}
            {!isRecieptSubmit && (
              <button
                disabled={isLoading || sessionId}
                onClick={() => {
                  startPaymentRequest();
                }}
                className="apply-field mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dd7538] focus:ring focus:ring-[#dd7538] focus:ring-opacity-50 hover:bg-orange-400"
              >
                Online Payment
              </button>
            )}
            <div
              className={`w-full p-6  mt-4 rounded-lg ${
                sessionId && "bg-white"
              }`}
            >
              <div className="w-full" id="embed-target"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const CancelMessage = () => {
  return (
    <div className="flex flex-col justify-center items-center">
      <p
        className="text-white pt-4 pb-2 font-semibold text-center"
        data-aos="fade-up"
        data-aos-delay="100"
        data-aos-once="true"
      >
        Payment process cancelled!
        <br />
        <br />
        If you want to try again, kindly Start process from beginning by
        clicking below button. Or if you need any assistant reach out to us via
        <a href="tel:0117543945" className="text-[#dd7538]">
          {" "}
          0117543945
        </a>{" "}
        or send us an email to{" "}
        <a
          href="mailto:info@nationaleducatorawards.lk"
          className="text-[#dd7538]"
        >
          info@nationaleducatorawards.lk
        </a>
      </p>
      <Link
        to={"/apply"}
        className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
      >
        Start From Beginnning
      </Link>
    </div>
  );
};

export const TimeoutMessage = () => {
  return (
    <div className="flex flex-col justify-center items-center">
      <p
        className="text-white pt-4 pb-2 font-semibold text-center"
        data-aos="fade-up"
        data-aos-delay="100"
        data-aos-once="true"
      >
        Payment process timeout!
        <br />
        <br />
        If you want to try again, kindly Start process from beginning by
        clicking below button. Or if you need any assistant reach out to us via
        <a href="tel:0117543945" className="text-[#dd7538]">
          {" "}
          0117543945
        </a>{" "}
        or send us an email to{" "}
        <a
          href="mailto:info@nationaleducatorawards.lk"
          className="text-[#dd7538]"
        >
          info@nationaleducatorawards.lk
        </a>
      </p>
      <Link
        to={"/apply"}
        className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
      >
        Start From Beginnning
      </Link>
    </div>
  );
};

export const ApplicationsClosedMessage = () => {
  return (
    <div className="flex flex-col justify-center items-center">
      <p
        className="text-white pt-4 pb-2 font-semibold text-center"
        data-aos="fade-up"
        data-aos-delay="100"
        data-aos-once="true"
      >
        Applications are now closed.
        <br />
        <br />
        The application deadline for the General Education, Higher Education and
        Emerging Educator categories has passed, so new applications can no
        longer be submitted.
        <br />
        <br />
        You are welcome to explore the award categories and view our past
        winners. If you have any questions or need further assistance, feel free
        to contact our support team through phone
        <a href="tel:0117543945" className="text-[#dd7538]">
          {" "}
          0117543945
        </a>{" "}
        or send us an email to{" "}
        <a
          href="mailto:info@nationaleducatorawards.lk"
          className="text-[#dd7538]"
        >
          info@nationaleducatorawards.lk
        </a>
      </p>
      <Link
        to={"/"}
        className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
      >
        Go to Home
      </Link>
    </div>
  );
};

export const SuccesstMessage = ({ selectedCat }) => {
  return (
    <div className="flex flex-col justify-center items-center">
      <p
        className="text-white pt-4 pb-2 font-semibold text-center"
        data-aos="fade-up"
        data-aos-delay="100"
        data-aos-once="true"
      >
        Thank you for your payment!
        <br />
        We have sent a confirmation email to your registered e-mail address.
        <br />
        <br />
        {selectedCat !== "sc" && (
          <>
            Please follow the instructions in your email to submit your
            application for National Educator Awards 2026.
            <br />
          </>
        )}
        Please ensure to check both your inbox and spam folder for our response.
        <br />
        If you have any questions or need further assistance, feel free to
        contact our support team through phone
        <a href="tel:+94117543945" className="text-[#dd7538]">
          {" "}
          0117543945
        </a>{" "}
        or send us an email to{" "}
        <a
          href="mailto:info@nationaleducatorawards.lk"
          className="text-[#dd7538]"
        >
          info@nationaleducatorawards.lk
        </a>
      </p>
      <Link
        to={"/"}
        className="px-4 py-2 mt-4 bg-[#dd7538] text-white rounded-sm hover:bg-[#ff914d] focus:outline-none focus:bg-[#ff914d]"
      >
        Go to Home
      </Link>
    </div>
  );
};
